import React from "react";
import * as UI from "./ui.jsx";
// Inspections — inspector-first entry, weighted typeahead with spelling tolerance,
// optional agent, horse multi-select, auto repeat detection; recent activity alongside.

const { useState: useStateI, useMemo: useMemoI, useRef: useRefI } = React;

// ---- Fuzzy, recency-weighted buyer matching (window.BUYERS from the inspections module) ----
function editDist(a, b, cap = 3) {
  if (Math.abs(a.length - b.length) > cap) return cap + 1;
  const dp = Array.from({ length: a.length + 1 }, (_, i) => [i, ...Array(b.length).fill(0)]);
  for (let j = 0; j <= b.length; j++) dp[0][j] = j;
  for (let i = 1; i <= a.length; i++) for (let j = 1; j <= b.length; j++)
    dp[i][j] = Math.min(dp[i - 1][j] + 1, dp[i][j - 1] + 1, dp[i - 1][j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
  return dp[a.length][b.length];
}
function matchBuyers(q, limit = 6) {
  const buyers = window.BUYERS || [];
  if (!q.trim()) return buyers.slice(0, limit);
  const qt = q.trim().toLowerCase();
  const scored = [];
  for (const b of buyers) {
    const name = b.name.toLowerCase();
    let m = 0;
    if (name.startsWith(qt)) m = 1.2;
    else if (name.includes(qt)) m = 1.0;
    else {
      // token-level prefix / small-edit tolerance (catches misspellings)
      const tokens = name.split(/[\s,\-.]+/);
      for (const t of tokens) {
        if (t.startsWith(qt)) { m = Math.max(m, 0.9); }
        else if (qt.length >= 4 && editDist(qt, t.slice(0, qt.length + 1)) <= (qt.length > 6 ? 2 : 1)) m = Math.max(m, 0.6);
      }
    }
    if (m > 0) scored.push({ b, s: m * (1 + b.w) });
  }
  return scored.sort((x, y) => y.s - x.s).slice(0, limit).map(x => x.b);
}

function InspectView({ go, preselectId }) {
  const { Icon, Card, Tag, BoxSnap, useBoxAssign, demoTimeHM, ModuleGate, MockNote } = UI;
  const { HORSES } = window.DATA;
  const [assign] = useBoxAssign();

  const [step, setStep] = useStateI(0);           // 0: inspector · 1: horses
  const [buyer, setBuyer] = useStateI("");
  const [agent, setAgent] = useStateI("");
  const [sel, setSel] = useStateI(preselectId ? [preselectId] : []);
  const [time, setTime] = useStateI(demoTimeHM());
  const [notes, setNotes] = useStateI("");
  const [saved, setSaved] = useStateI(false);
  const skipHorses = !!preselectId;

  const repeat = useMemoI(() =>
    !!buyer.trim() && window.DATA.INSPECTIONS.some(i => i.buyer.toLowerCase() === buyer.trim().toLowerCase()),
  [buyer]);

  const toggle = (id) => setSel(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  const selHorses = sel.map(id => HORSES.find(h => h.id === id)).filter(Boolean);

  // No inspections provider exists yet, so a logged visit lives in this page's
  // memory for the length of the session. It is not written anywhere.
  const save = () => {
    window.DATA.INSPECTIONS.unshift({
      id: "i" + Date.now(), time, buyer: buyer.trim(), agent: agent.trim(),
      horses: [...sel], by: window.DATA.VENDOR.staff, repeat, notes: notes.trim(),
    });
    setSaved(true);
  };
  const reset = () => { setSaved(false); setStep(0); setBuyer(""); setAgent(""); setSel([]); setNotes(""); setTime(demoTimeHM()); };

  if (saved) {
    return (
      <div className="fade-in" style={{ display: "grid", placeItems: "center", padding: "60px 0" }}>
        <div className="card" style={{ textAlign: "center", padding: "48px 40px", maxWidth: 560 }}>
          <div style={{ width: 68, height: 68, margin: "0 auto 16px", background: "#EAF3EE", border: "1px solid var(--positive)", borderRadius: "50%", display: "grid", placeItems: "center" }}>
            <Icon.check style={{ width: 30, height: 30, color: "var(--positive)" }}/>
          </div>
          <div className="display" style={{ fontSize: 32 }}>Inspection logged</div>
          <div className="muted" style={{ marginTop: 8, lineHeight: 1.5 }}>
            <span className="display-italic" style={{ color: "var(--inglis-blue-dark)" }}>{buyer}</span>
            {agent && <> ({agent})</>} inspected {selHorses.map(h => `Lot ${h.lot}`).join(", ")} at {time}.
            {repeat && <> Repeat visit.</>}
          </div>
          <div className="caption" style={{ marginTop: 12 }}>Held in this session only — there is no inspections provider yet.</div>
          <div className="row" style={{ gap: 10, marginTop: 24, justifyContent: "center" }}>
            <button className="btn primary" onClick={reset}>Log another</button>
            <button className="btn" onClick={() => go("stable")}>Back to stable</button>
          </div>
        </div>
      </div>
    );
  }

  const STEPS = skipHorses ? ["Who's inspecting"] : ["Who's inspecting", "Which horses"];

  return (
    <ModuleGate modules="inspections">
      <div className="fade-in">
        <div className="inspect-grid">
          <div>
            <div className="steps" style={{ maxWidth: 520 }}>
              {STEPS.map((s, i) => (
                <div key={s} className={`step ${i === step ? "active" : i < step ? "done" : ""}`}>
                  <span className="n">{i < step ? "✓" : i + 1}</span> {s}
                </div>
              ))}
            </div>

            {step === 0 && (
              <Card title="Who's inspecting" subtitle="Start typing — the most likely visitors surface first, spelling slips included.">
                <Typeahead label="Buyer / inspector" value={buyer} onChange={setBuyer} autoFocus
                           placeholder="e.g. Suman Hedge Bloodstock, Paul Moroney…"/>
                {repeat && <div className="row" style={{ gap: 8, marginTop: -8, marginBottom: 12 }}><Tag variant="gold">Repeat visit</Tag><span className="muted" style={{ fontSize: 12 }}>Seen earlier this sale.</span></div>}
                <Typeahead label="Agent / representative" optional value={agent} onChange={setAgent}
                           placeholder="Optional — who's walking for them"/>
                <div className="row fieldrow" style={{ gap: 16 }}>
                  <div className="field" style={{ flex: "0 0 130px" }}>
                    <label>Time</label>
                    <input type="time" value={time} onChange={e => setTime(e.target.value)}/>
                  </div>
                  <div className="field" style={{ flex: 1 }}>
                    <label>Notes <span style={{ fontWeight: 400, color: "var(--fg-muted)" }}>(optional)</span></label>
                    <input value={notes} onChange={e => setNotes(e.target.value)} placeholder="e.g. asked for breeze time, wants vet report…"/>
                  </div>
                </div>
                <div className="row" style={{ justifyContent: "space-between", marginTop: 8 }}>
                  <div className="muted" style={{ fontSize: 12 }}>Logged by {window.DATA.VENDOR.staff}</div>
                  {skipHorses ? (
                    <button className="btn primary lg" disabled={!buyer.trim()} onClick={save}><Icon.check style={{ width: 15, height: 15 }}/> Log inspection</button>
                  ) : (
                    <button className="btn primary lg" disabled={!buyer.trim()} onClick={() => setStep(1)}>Choose horses <Icon.arrow style={{ width: 15, height: 15 }}/></button>
                  )}
                </div>
              </Card>
            )}

            {step === 1 && (
              <div>
                <div className="section-head">
                  <h2>Which horses did {buyer.split(" ")[0]} see?</h2>
                  <span className="muted">{sel.length} selected</span>
                </div>
                <div className="grid cols-4 pickgrid">
                  {HORSES.filter(h => !h.withdrawn).map(h => {
                    const on = sel.includes(h.id);
                    return (
                      <button key={h.id} className="horse-card pick" data-on={on ? "1" : undefined} onClick={() => toggle(h.id)}>
                        <div className="photo" style={{ height: 92 }}>
                          <BoxSnap box={assign[h.lot]} meta={false} style={{ position: "absolute", inset: 0 }}/>
                          <span className="lot">LOT {h.lot}</span>
                          {on && <span className="pickmark"><Icon.check style={{ width: 14, height: 14 }}/></span>}
                        </div>
                        <div className="body" style={{ padding: "10px 12px" }}>
                          <div style={{ fontSize: 13, fontWeight: on ? 700 : 400 }}>{h.name}</div>
                          <div className="muted" style={{ fontSize: 10.5, marginTop: 2 }}>{h.sex}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
                <div className="row" style={{ justifyContent: "space-between", marginTop: 22 }}>
                  <button className="btn ghost" onClick={() => setStep(0)}><Icon.back style={{ width: 14, height: 14 }}/> Back</button>
                  <button className="btn primary lg" disabled={sel.length === 0} onClick={save}>
                    <Icon.check style={{ width: 15, height: 15 }}/> Log {sel.length || ""} {sel.length === 1 ? "horse" : "horses"}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Review side */}
          <div className="inspect-side">
            <Card title="This morning" subtitle="Across the draft" right={<MockNote module="inspections"/>}>
              {window.DATA.INSPECTIONS.slice(0, 6).map((i, idx) => {
                const lots = i.horses.map(id => HORSES.find(h => h.id === id)?.lot).filter(Boolean);
                return (
                  <div key={i.id} style={{ padding: "11px 0", borderTop: idx ? "1px solid var(--divider)" : "0" }}>
                    <div className="row" style={{ justifyContent: "space-between" }}>
                      <span style={{ fontSize: 13.5 }}>{i.buyer}</span>
                      <span className="mono muted" style={{ fontSize: 11 }}>{i.time}</span>
                    </div>
                    <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>
                      {lots.length > 1 ? `Lots ${lots.join(", ")}` : `Lot ${lots[0]}`}{i.repeat ? " · repeat" : ""}{i.agent ? ` · ${i.agent}` : ""}
                    </div>
                  </div>
                );
              })}
            </Card>
          </div>
        </div>
      </div>
    </ModuleGate>
  );
}

// ---- Weighted typeahead field ----
function Typeahead({ label, value, onChange, placeholder, autoFocus, optional }) {
  const [open, setOpen] = useStateI(false);
  const [hi, setHi] = useStateI(0);
  const wrap = useRefI(null);
  const hits = useMemoI(() => matchBuyers(value), [value]);
  const pick = (name) => { onChange(name); setOpen(false); };
  return (
    <div className="field" ref={wrap} style={{ position: "relative" }}>
      <label>{label} {optional && <span style={{ fontWeight: 400, color: "var(--fg-muted)" }}>(optional)</span>}</label>
      <input value={value} autoFocus={autoFocus} placeholder={placeholder}
             onChange={e => { onChange(e.target.value); setOpen(true); setHi(0); }}
             onFocus={() => setOpen(true)}
             onBlur={() => setTimeout(() => setOpen(false), 150)}
             onKeyDown={e => {
               if (!open || !hits.length) return;
               if (e.key === "ArrowDown") { e.preventDefault(); setHi(h => Math.min(h + 1, hits.length - 1)); }
               if (e.key === "ArrowUp") { e.preventDefault(); setHi(h => Math.max(h - 1, 0)); }
               if (e.key === "Enter") { e.preventDefault(); pick(hits[hi].name); }
               if (e.key === "Escape") setOpen(false);
             }}/>
      {open && hits.length > 0 && (
        <div className="ta-menu">
          {hits.map((b, i) => (
            <button key={b.name} className={i === hi ? "hi" : ""} onMouseDown={e => { e.preventDefault(); pick(b.name); }}>
              <span>{b.name}</span>
              <span className="muted" style={{ fontSize: 10.5 }}>{b.visits} visit{b.visits === 1 ? "" : "s"} · last {b.last}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export { InspectView };
